import { useEffect, useState } from 'react';
import { AlertDialog, DialogFormV2, selectUser } from '@centerpiece/components';
import { useSelector } from 'react-redux';
import {
  contract_type,
  useCreateProjectMutation,
  useGetContactQuery,
  useGetSubscriberQuery,
} from 'store/api';
import {
  Attachment,
  Contact,
  Project,
  ProjectContact,
  Subscriber,
} from 'store/api/models';
import {
  CreateProjectStatus,
  CreateProjectStatusProps,
} from '../statuses/CreateProjectStatus';
import {
  useFESProjectForm,
  useCustomProjectForm,
} from 'jformik/useProjectForm';
import { useNavigate } from 'react-router-dom';
import { createProjectSteps } from 'tutorials/createProject.tutorial';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { ProjectPreviewTab } from 'drawers/tabs/ProjectPreviewTab';
import { ContactComponent } from 'components/ContactComponent';
import { useMasterProjectForm } from 'jformik/useMasterProjectForm';

export interface CreateProjectDialogueProps {
  open: boolean;
  close: Function;
  category?: string;
}

export const CreateProjectDialogue = ({
  open,
  close,
  category,
}: CreateProjectDialogueProps) => {
  const navigate = useNavigate();
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    initialValues?: any;
  }>({ open: false });
  const user = useSelector(selectUser);
  const { data: subscriber = {} as Subscriber, isLoading } =
    useGetSubscriberQuery(
      { id: user.subscriber, params: { nested: 'all' } },
      { skip: !user.subscriber }
    );
  const { contact_r: contact, role_r: role } = subscriber;
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [preview, setPreview] = useState<number>();
  const [createProject] = useCreateProjectMutation();
  const [projectStatus, setProjectStatus] = useState<
    Omit<CreateProjectStatusProps, 'open' | 'close'>
  >({
    loading: false,
    creatingProject: false,
    creatingProjectError: false,
    contacts: { pending: 0, created: 0, failed: 0 },
    attachments: { pending: 0, created: 0, failed: 0 },
  });
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [fesValidation, fesForm] = useFESProjectForm({
    setPreview,
    setOpenContact,
  });
  // const [customValidation, customForm] = useCustomProjectForm({
  //   setPreview,
  //   setOpenContact,
  // });
  const [customValidation, customForm] = useMasterProjectForm({
    setPreview,
    setOpenContact,
  });
  const { state, setState } = useTutorialContext();
  useEffect(() => {
    if (open && state.tourActive) {
      setState(currState => ({
        ...currState,
        run: true,
      }));
    }
    // eslint-disable-next-line
  }, [open]);
  return (
    <>
      <CreateProjectStatus
        open={openStatus}
        close={() => setOpenStatus(false)}
        {...projectStatus}
      />
      <AlertDialog
        open={openAlert}
        title={'Exit Creating Project?'}
        content={'Are you sure you would like to exit creating a project?'}
        onClose={() => {
          setOpenAlert(false);
        }}
        onConfirm={() => {
          close();
          setOpenContact({ open: false });
          setOpenAlert(false);
        }}
      />
      <DialogFormV2
        open={open}
        onHelp={
          category === 'Custom'
            ? undefined
            : () =>
                setState({
                  run: true,
                  stepIndex: 0,
                  steps: createProjectSteps(),
                })
        }
        title={'Create a New Project'}
        onClose={() => setOpenAlert(true)} //close()}
        subtitle={`Owner: @${user?.firstname} ${user?.lastname}`}
        fullWidth={true}
        maxWidth={'md'}
        scroll={'body'}
        disableSubmitOnEnter={true}
        initialValues={() => ({
          title: '',
          stage: null,
          details: '',
          customer_name: '',
          customer_number: '',
          address: '',
          city: '',
          zipcode: '',
          due_date: '',
          install_date: '',
          contacts: [{ contact, role }],
          attachments: [],
          teams: contact?.form?.team ? [contact?.form?.team] : [],
        })}
        handleSubmit={async (values, formik) => {
          // const contacts = [
          //   ...(values?.contacts || []),
          //   ...(values?.internalcontacts || []),
          //   ...(values?.externalcontacts || []),
          // ].map((contact: any) => {
          //   if (contact?.contact?.id > 0) {
          //     return {
          //       ...contact,
          //       contact: { ...contact.contact, ...contact.contact.contact_r },
          //       role:
          //         contact.stakeholdertype.id > 0
          //           ? { ...(contact.stakeholdertype || {}) }
          //           : undefined,
          //     };
          //   } else {
          //     return {
          //       ...contact,
          //       contact: { ...contact.contact, email: contact.contact.name },
          //       role:
          //         contact.stakeholdertype.id > 0
          //           ? { ...(contact.stakeholdertype || {}) }
          //           : undefined,
          //     };
          //   }
          // });
          setProjectStatus({
            loading: true,
            creatingProject: true,
            creatingProjectError: false,
            contacts: {
              pending:
                values.contacts?.length > 0 ? values.contacts?.length : 0,
              created: 0,
              failed: 0,
            },
            attachments: {
              pending:
                values.attachments?.length > 0 ? values.attachments?.length : 0,
              created: 0,
              failed: 0,
            },
          });
          setOpenStatus(true);
          try {
            const newProject: Project = await createProject({
              ...values,
              // contacts: contacts,
              // organizations: values.stakeholders.filter(
              //   (s: any) => s.contact?.type === 'organization'
              // ),
              stakeholders: values?.stakeholders
                // .filter((s: any) => s.contact?.type !== 'organization')
                ?.map((contact: any) => {
                  if (contact.contact?.type === 'organization') {
                    return {
                      ...contact,
                      organization: {
                        ...contact.contact,
                        ...contact.contact.contact_r,
                      },
                      contact: null,
                    };
                  } else {
                    return {
                      ...contact,
                      contact: {
                        ...contact.contact,
                        ...contact.contact.contact_r,
                      },
                    };
                  }
                }),
              type: values?.type?.id,
              contract_type: values?.contract_type?.id,
              market_segment: values?.market_segment?.id,
              template: values?.template?.template,
              category: category,
            }).unwrap();
            if (newProject.id > 0) {
              setProjectStatus(currentStatus => ({
                ...currentStatus,
                creatingProject: false,
                contacts: {
                  pending: 0,
                  created: newProject?.contacts?.filter(
                    (c: ProjectContact) => !!c?.id
                  ).length,
                  failed: newProject?.contacts?.filter(
                    (c: ProjectContact) => !!!c?.id
                  ).length,
                },
              }));
              if (values?.attachments?.length > 0) {
                for (let i = 0; i < values.attachments.length; i++) {
                  const attachment: Attachment = newProject.attachments[i];
                  const file = values.attachments?.find(
                    (a: File) => a.name === attachment.name
                  );
                  const res = await fetch(attachment.url, {
                    method: 'PUT',
                    body: file.file,
                  });
                  if (res.ok) {
                    setProjectStatus(currentStatus => ({
                      ...currentStatus,
                      attachments: {
                        pending: currentStatus.attachments.pending - 1,
                        created: currentStatus.attachments.created + 1,
                        failed: currentStatus.attachments.failed,
                      },
                    }));
                  } else {
                    setProjectStatus(currentStatus => ({
                      ...currentStatus,
                      attachments: {
                        pending: currentStatus.attachments.pending - 1,
                        created: currentStatus.attachments.created,
                        failed: currentStatus.attachments.failed + 1,
                      },
                    }));
                  }
                }
              }
              setProjectStatus(currentStatus => ({
                ...currentStatus,
                loading: false,
              }));
            } else {
              setProjectStatus(currentStatus => ({
                ...currentStatus,
                creatingProject: false,
                creatingProjectError: true,
              }));
            }
            close();
            navigate(`/project/${newProject?.id}`);
          } catch (error) {
            setProjectStatus(currentStatus => ({
              ...currentStatus,
              creatingProject: false,
              creatingProjectError: true,
            }));
            console.error('rejected', error);
          }
        }}
        validationSchema={category === 'FES' ? customValidation : fesValidation}
        form={category === 'FES' ? customForm : fesForm}
        alternate={
          openContact.open ? (
            <ContactComponent
              close={() => setOpenContact({ open: false })}
              {...openContact}
            />
          ) : (
            <ProjectPreviewTab
              id={preview || 0}
              template={true}
              disabled={true}
              onBack={() => setPreview(undefined)}
              header={true}
            />
          )
        }
        openAlternate={Number(preview) > 0 || openContact.open}
        closeAlternate={() => setPreview(undefined)}
      />
    </>
  );
};
